import { configureDataHooks } from '@vnetwork/datacentral-react'
import { Spinner } from '@vnetwork/datacentral-ui/primitives'
import { toast } from '@vnetwork/datacentral-ui/feedback'
import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import { TableSkeleton } from '@/components/TableSkeleton'
import QueryErrorAlert from '@/components/ErrorBoundary/QueryErrorAlert'
import TableErrorState from '@/components/TableErrorState'
import { QUERY_KEYS, type QueryKey } from '@/constants/reactQueryKeys'

/**
 * Wire the app into `@vnetwork/datacentral-react` (issue #58), the same way
 * `@/utils/axios/register-core` wires it into core's HTTP registry.
 *
 * The hooks live outside the app now, so they cannot import `ErrorPage`,
 * `TableSkeleton`, the UI kit's `toast`, or `QUERY_KEYS`. The app hands all of
 * them over here instead, once, at bootstrap.
 */

/**
 * Contribute the app's query keys to the package's registry, which restores
 * the exact compile-time constraint the hooks had before the move: the first
 * element of a `queryKey`, and every `reactQueryKeys` value, must be one of
 * `QUERY_KEYS`. Without this augmentation the package falls back to `string`
 * and all 800+ call sites would silently lose that check.
 *
 * An extracted module adds its own keys with a second `declare module` block
 * of the same shape — interface declarations merge, so the contributions add
 * up instead of competing.
 */
declare module '@vnetwork/datacentral-react' {
    interface QueryKeyRegistry extends Record<QueryKey, true> {}
}

configureDataHooks({
    renderLoading: () => <Spinner />,
    renderTableLoading: ({ rows, columns }) => (
        <TableSkeleton rows={rows} columns={columns} />
    ),
    renderQueryError: ({ error, onRetry }) => (
        <QueryErrorAlert error={error} onRetry={onRetry} />
    ),
    renderPageError: ({ status, error }) => (
        <ErrorPage status={status} error={error} />
    ),
    renderTableError: ({ error, onRetry }) => (
        <TableErrorState error={error} onRetry={onRetry} />
    ),
    // Wrapped rather than passed by reference: `toast.success` is a method on
    // the UI kit's toast object and must keep its receiver.
    notify: {
        success: (message) => toast.success(message),
        error: (message) => toast.error(message),
    },
    // Was hard-coded inside useMutationWithHandler: every successful mutation
    // refreshes the activities feed.
    alwaysInvalidateKeys: [QUERY_KEYS.ACTIVITIES],
})
